












































































































































































































































import mixins from 'vue-typed-mixins';
import LocationBasedMixin from '@/Shared/LocationBasedMixin';
import { Citation } from '@/Citation/Citation';
import VatsAPI from '@/Shared/VatsAPI';
import { HostedCheckoutRequest } from '@/Payment/HostedCheckoutRequest';
import { Dictionary, Route } from 'vue-router/types/router';
import axios, { AxiosResponse } from 'axios';
import { PaymentResult } from '@/Payment/PaymentResult';
import { PaymentProfile } from './PaymentProfile';
import { PaymentMethod, PaymentSource } from '@/Payment/Enums';
import { getPaymentMethodComponent } from '@/Payment/PaymentMethods/GetPaymentMethodComponent';
import { User, Location } from '@/Stores/UserStore';
import PaymentMethods from '@/Payment/PaymentMethods/PaymentMethods.vue';
import PaymentCompletion from '@/Citation/Payment/PaymentCompletion.vue';
import { TranslateResult } from 'vue-i18n';
import { CustomerRequiredFields } from '@/Customer/CustomerRequiredFields';
import Customer from '@/Customer/Customer';
import { typedStore } from '@/store';
import { stringCompare } from '@/Shared/Utilities';
import { LocationLookupType } from '@/Stores/LookupStore';
import UserMixin from '@/Shared/UserMixin';

// When updating, keep in sync with CitationCustomerValidator required fields
// serverside, in NeoVats.Business/Citation/CitationCustomerValidator
const externalCustomerRequiredFields: Partial<CustomerRequiredFields> = {
    emailAddress: true,
};

const internalCustomerRequiredFields: Partial<CustomerRequiredFields> = {
    emailAddress: false,
};

interface SearchParams {
    citation: string | null;
    plateNumber: string | null;
    state: string | null;
}

function isPaymentProfile(obj: any): obj is PaymentProfile {
    if (typeof obj.status === 'object' && Array.isArray(obj.messages)) {
        return true;
    }
    return false;
}

function shouldRedirectToLegacy(location: Location): boolean {
    // MBTA and Hartford
    return location.locationParentID === 47 || location.locationParentID === 2;
}

function redirectToLegacy(queryParams: any, location: Location) {
    const params = {
        ...queryParams,
        location: location.locationParentID,
        recid: queryParams.citation
    } as any;

    const queryString = Object.keys(params)
        .filter(param => params[param])
        .map(param => `${encodeURIComponent(param)}=${encodeURIComponent(params[param])}`)
        .join('&');

    window.location.replace(`/payments/payments_legacy.aspx?${queryString}`);
}

const component = mixins(LocationBasedMixin, UserMixin).extend({
    components: {
        PaymentMethods,
        PaymentCompletion,
        CustomerEdit: () => import('@/Customer/CustomerEdit.vue'),
    },
    async beforeRouteEnter(to: Route, from: Route, next) {
        const location = to.params.location;
        const locations = await typedStore.modules.userModule.actions.getAllowedLocations();
        const selectedLocation = locations
            .find(item => stringCompare(item.urlSegment, location));
        if (!selectedLocation) {
            next({
                name: 'citationPaymentNoLocation',
                params: {
                    // silly type games
                    unknownLocation: true as any as string,
                    location
                },
            });
        }
        else if (shouldRedirectToLegacy(selectedLocation)) {
            redirectToLegacy(to.query, selectedLocation);
        }
        else {
            next();
        }
    },
    async beforeMount() {
        await this.loadUser();

        const selectedLocationPromise = this.selectedLocation;
        if (this.searchState) {
            this.enteredSearchState = this.searchState.toUpperCase();
        }
        if (this.searchCitationIdProp
            || (this.searchPlate && this.searchState)) {
            this.searchCitationId = this.searchCitationIdProp;
            this.searchCitations();
        }

        if (!this.isInternal) {
            this.selectedPaymentSource = PaymentSource.Online;
            this.selectedPaymentMethod = PaymentMethod.Card;
        }

        const selectedLocation = await selectedLocationPromise;
        if (selectedLocation) {
            this.$typedStore.actions.changeCurrentLocation(selectedLocation);
        }
    },
    data() {
        return {
            profile: { citations: [] as Citation[] } as PaymentProfile,
            i18nPrefix: 'payment.citationPayment',
            loadingCitations: false,
            refreshingProfile: false,
            searchCitationId: null as string | null,
            enteredSearchCitation: this.searchCitationIdProp as string | null,
            enteredSearchPlate: this.searchPlate as string | null,
            enteredSearchState: this.searchState as string | null,
            selectedToPay: {} as Dictionary<boolean>,
            toggleState: true,
            paymentProcessing: false,
            PaymentMethod,
            paymentMethodDisplay: Object.keys(PaymentMethod),
            selectedPaymentMethod: PaymentMethod.Card as PaymentMethod | null,
            PaymentSource,
            paymentSourceDisplay: Object.keys(PaymentSource),
            selectedPaymentSource: null as PaymentSource | null,
            result: null as PaymentResult | null,
            validationErrors: {} as Dictionary<Array<(string | TranslateResult)>>,
            customer: null as Customer | null,
            customerErrors: null as Dictionary<string[]> | null,
            lastSearchParams: {} as SearchParams,
            LocationLookupType,
        };
    },
    props: {
        searchCitationIdProp: {
            required: false,
            type: String as () => string | null,
        },
        searchPlate: {
            required: false,
            type: String as () => string | null,
        },
        searchState: {
            required: false,
            type: String as () => string | null,
        },
    },
    computed: {
        canSelectPaymentMethod(): boolean {
            return this.selectedPaymentSource !== PaymentSource.CallIn;
        },
        payableQueryUrl(): string {
            return `${this.location}/CitationPayments/${this.selectedPaymentMethod || PaymentMethod.Card}/payable`;
        },
        additionalCitations(): Citation[] {
            return this.profile.additionalCitations || [];
        },
        selectedPaymentSubtotal(): number {
            const subtotal = this.profile.citations.concat(this.additionalCitations)
            .filter(item => this.selectedToPay[item.recID])
            .reduce((total, citation) =>
                total + (citation?.remainingBalance || 0) - this.citationDiscount(citation), 0);
            return subtotal;
        },
        selectedPaymentsTax(): number {
            const totalTax = this.profile.citations.concat(this.additionalCitations)
                .filter(item => this.selectedToPay[item.recID] && this.profile.taxes[item.recID])
                .map(citation => this.profile.taxes[citation.recID].reduce((total, tax) => tax.amount + total, 0))
                .reduce((total, tax) => total + tax, 0);
            return totalTax;
        },
        selectedPaymentTotal(): number {
            return this.selectedPaymentSubtotal + this.profile.fee + this.selectedPaymentsTax;
        },
        citationsToPay(): string[] {
            return this.profile.citations
                    .filter(item => this.selectedToPay[item.recID])
                    .map(item => item.recID)
                    .concat(
                        this.additionalCitations
                        .filter(item => this.selectedToPay[item.recID])
                        .map(item => item.recID)
                        );
        },
        showPaymentMethods(): boolean {
            return this.selectedPaymentSource !== null
                && this.selectedPaymentMethod !== null
                && this.profile.citations.length > 0;
        },
        blockPayment(): boolean {
            return this.profile.blocked || !this.anyPaymentsSelected;
        },
        anyPaymentsSelected(): boolean {
            return this.profile.citations.find(item => this.selectedToPay[item.recID]) !== undefined
                || this.additionalCitations.find(item => this.selectedToPay[item.recID]) !== undefined;
        },
        requiredToPay(): Dictionary<boolean> {
            return this.profile.requiredToPay.reduce((dict, citationId) => {
                dict[citationId] = true;
                this.selectedToPay[citationId] = true;
                return dict;
            }, {} as Dictionary<boolean>);
        },
        customerRequiredFields(): Partial<CustomerRequiredFields> {
            return (this.isInternal) ? internalCustomerRequiredFields : externalCustomerRequiredFields;
        },
        statusMessages(): string[] {
            const messages: string[] = [];
            if (!this.profile || !this.profile.status || this.loadingCitations) {
                return messages;
            }

            const getText = (textName: string) =>
                this.$t(`${this.i18nPrefix}.${textName}`, this.lastSearchParams).toString();

            if (this.profile.status.noResultsFound) {
                if (this.lastSearchParams.citation) {
                    messages.push(getText('citationIdNotFoundMessage'));
                }
                else if (this.lastSearchParams.state && this.lastSearchParams.plateNumber) {
                    messages.push(getText('vehicleNotFoundMessage'));
                }
                else {
                    messages.push(getText('genericNotFoundMessage'));
                }
            }

            if (this.profile.status.alreadyPaid) {
                messages.push(getText('citationAlreadyPaidMessage'));
            }

            if (this.profile.status.alreadyVoided) {
                messages.push(getText('citationAlreadyVoidedMessage'));
            }

            if (this.profile.status.userCannotAcceptPayments) {
                messages.push(getText('userCannotAcceptPaymentsMessage'));
            }

            if (this.profile.status.requiredCitationsNotSelected) {
                messages.push(getText('requiredCitationsNotSelectedMessage'));
            }

            return messages;
        },
        async selectedLocation(): Promise<Location | undefined> {
            const locations = await typedStore.modules.userModule.actions.getAllowedLocations();
            const selectedLocation = locations
                .find(item => stringCompare(item.urlSegment, this.location));
            return selectedLocation;
        }
    },
    methods: {
        async searchCitations(): Promise<void> {
            this.result = null;

            const validation = this.validateSearchOptions();
            this.validationErrors = validation || {};
            if (validation) {
                return;
            }

            this.loadingCitations = true;

            await this.updateQueryParams();

            try {
                this.lastSearchParams = {
                    citation: this.enteredSearchCitation,
                    plateNumber: this.enteredSearchPlate,
                    state: this.enteredSearchState
                };

                const response = await VatsAPI.get<PaymentProfile>(this.payableQueryUrl, {
                    params: {
                        recID: this.enteredSearchCitation,
                        plate: this.enteredSearchPlate,
                        state: this.enteredSearchState,
                        paymentSource: this.selectedPaymentSource || PaymentSource.Online
                    }
                });
                this.profile = response.data;
                this.profile.citations.sort((a, b) => a.recID.localeCompare(b.recID));
                const toPay = this.profile.citations.reduce((map, citation) => {
                    map[citation.recID] = true;
                    return map;
                }, {} as Dictionary<boolean>);
                this.selectedToPay = (this.profile.additionalCitations || []).reduce((map, citation) => {
                    map[citation.recID] = false;
                    return map;
                }, toPay);
                this.customer = this.profile.vehicleOwner || new Customer();
                this.$nextTick(this.updateToggleState);
            }
            catch (e) {
                const translatedTitle = this.$i18n.t(`${this.i18nPrefix}.loadFailedTitle`);
                const translatedMessage = this.$i18n.t(`${this.i18nPrefix}.loadFailedMessage`);
                this.$bvToast.toast(translatedMessage.toString(), {
                    title: translatedTitle.toString(),
                    autoHideDelay: 5000
                });
                throw e;
            }
            finally {
                this.loadingCitations = false;
            }
        },
        async refreshPaymentProfile() {
            if (this.selectedPaymentSource === PaymentSource.CallIn) {
                this.selectedPaymentMethod = PaymentMethod.Card;
            }

            if (this.selectedPaymentMethod && this.selectedPaymentSource) {
                this.refreshingProfile = true;
                try {
                    const url = `${this.location}/CitationPayments/${this.selectedPaymentMethod}/FeeAndDiscount`;
                    const result = await VatsAPI.get<{ item1: number, item2: number }>(url, {
                        params: {
                            paymentSource: this.selectedPaymentSource
                        }
                    });

                    const { item1, item2 } = result.data;
                    this.profile.fee = item1;
                    this.profile.discount = item2;
                }
                catch (e) {
                    // TODO
                }
                finally {
                    this.refreshingProfile = false;
                }
            }
        },
        paymentSubmitting() {
            this.loadingCitations = true;
        },
        async paymentSucceeded(result: PaymentResult) {
            this.result = result;

            if (result.hostedCheckoutRequest) {
                return;
            }

            // Don't unset everything if payment failed
            if (!this.result.success) {
                return;
            }

            this.loadingCitations = false;
            this.enteredSearchCitation = null;
            this.profile = { citations: [] as Citation[] } as PaymentProfile;
            this.selectedPaymentMethod = null;
            this.selectedPaymentSource = null;
            this.customerErrors = null;

            await this.$router.replace({
                name: this.$route.name ?? undefined
            });
        },
        paymentFailed(e: any) {
            this.loadingCitations = false;
            this.paymentProcessing = false;
        },
        paymentErrored(e: any) {
            this.paymentProcessing = false;
            this.loadingCitations = false;
            const translatedTitle = this.$i18n.t(`${this.i18nPrefix}.paymentFailedTitle`);
            const translatedMessage = this.$i18n.t(`${this.i18nPrefix}.paymentFailedMessage`);
            this.$bvToast.toast(translatedMessage.toString(), {
                title: translatedTitle.toString(),
                autoHideDelay: 5000
            });

            if (isPaymentProfile(e?.response?.data)) {
                const profile = e.response.data as PaymentProfile;
                this.profile.status = profile.status;
                this.profile.messages = profile.messages;
                return;
            }

            if (!e?.response?.data?.errors) {
                throw e;
            }

            const prefixes = ['VehicleOwner', 'Customer'];
            this.customerErrors = Object.keys(e.response.data.errors)
                .filter(item => prefixes.indexOf(item.split('.')[0]) > -1)
                .reduce((errorObj, key) => {
                    const errorList = e.response.data.errors[key];
                    const propName = key.split('.')[1];
                    errorObj[propName] = errorList;
                    return errorObj;
                }, {} as Dictionary<string[]>);

            throw e;
        },
        toggleAllCitations() {
            const citationList = this.profile.citations.map(item => item.recID)
                .concat(this.additionalCitations.map(item => item.recID));
            for (const recID of citationList) {
                if (!this.requiredToPay[recID]) {
                    this.selectedToPay[recID] = this.toggleState;
                }
            }
        },
        async locationChanged() {
            const selectedLocation = await this.selectedLocation;
            if (selectedLocation && shouldRedirectToLegacy(selectedLocation)) {
                redirectToLegacy(this.$route.query, selectedLocation);
            }
            else if (this.enteredSearchCitation
                || (this.enteredSearchPlate && this.enteredSearchState)) {
                this.searchCitations();
            }
        },
        async updateQueryParams() {
            if (this.searchCitationId !== this.enteredSearchCitation
                    && this.enteredSearchCitation) {
                await this.$router.replace({
                    name: this.$route.name ?? undefined,
                    query: {
                        citation: this.enteredSearchCitation
                    }
                });
            }
            else if ((this.searchPlate !== this.enteredSearchPlate
                    || this.searchState !== this.enteredSearchState)
                    && this.enteredSearchPlate && this.enteredSearchState) {
                await this.$router.replace({
                    name: this.$route.name ?? undefined,
                    query: {
                        plate: this.enteredSearchPlate,
                        state: this.enteredSearchState
                    }
                });
            }
            else if (!this.enteredSearchCitation
                    && !this.enteredSearchPlate
                    && !this.enteredSearchState) {
                await this.$router.replace({
                    name: this.$route.name ?? undefined
                });
            }
        },
        validateSearchOptions(): (Dictionary<Array<(string | TranslateResult)>> | null) {
            if (!this.enteredSearchCitation && !this.enteredSearchPlate && !this.enteredSearchState) {
                const msg = this.$t(`${this.i18nPrefix}.searchTermsRequiredMessage`);
                return {
                    enteredSearchCitation: [msg],
                    enteredSearchPlate: [msg],
                    enteredSearchState: [msg],
                };
            }

            if (this.enteredSearchCitation && (this.enteredSearchPlate || this.enteredSearchState)) {
                const msg = this.$t(`${this.i18nPrefix}.searchByOnlyOneMessage`);
                return {
                    enteredSearchCitation: [msg],
                    enteredSearchPlate: [msg],
                    enteredSearchState: [msg],
                };
            }

            if (this.enteredSearchPlate && !this.enteredSearchState) {
                const msg = this.$t(`${this.i18nPrefix}.pleaseSelectStateMessage`);
                return {
                    enteredSearchState: [msg],
                };
            }

            if (!this.enteredSearchPlate && this.enteredSearchState) {
                const msg = this.$t(`${this.i18nPrefix}.pleaseEnterPlateMessage`);
                return {
                    enteredSearchPlate: [msg],
                };
            }

            return null;
        },
        clearVehicleSearch() {
            this.enteredSearchPlate = null;
            this.enteredSearchState = null;
            this.updateQueryParams();

            if (this.enteredSearchCitation) {
                return;
            }

            this.profile = { citations: [] as Citation[] } as PaymentProfile;
        },
        updateToggleState() {
            const checkbox = this.$refs.toggleAllCitations as HTMLInputElement;
            if (!checkbox) {
                return;
            }
            const citationCount = this.profile.citations.length + this.additionalCitations.length;
            checkbox.indeterminate = this.citationsToPay.length !== citationCount
                && this.citationsToPay.length !== 0;

            this.toggleState = this.citationsToPay.length > 0;
        },
        citationDiscount(citation: Citation): number {
            if (!citation.violationCode || !this.profile) {
                return 0;
            }

            const excluded = this.profile.violationsExcludedFromDiscount
                .find(code => citation.violationCode === code);
            return excluded ? 0 : this.profile.discount;
        },
    },
    watch: {
        citationsToPay() {
            this.updateToggleState();
        }
    }
});

export default component;
